import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, useResource, errorText } from "@/lib/api";
import ConfirmDelete from "@/components/ConfirmDelete";

const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

export default function FinancialYearsTab() {
  const api = useApi();
  const { data, reload } = useResource("/financial_years");
  const [year, setYear] = useState("");
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); await reload(); } catch (e) { toast.error(errorText(e)); }
  };
  const add = (e) => {
    e.preventDefault();
    const y = parseInt(year, 10);
    if (!y) return toast.error("Enter the starting year, e.g. 2027");
    run(() => api("post", "/financial_years", { start_year: y }), "Financial year added").then(() => setYear(""));
  };

  return (
    <section className="space-y-5" data-testid="financial-year-section">
      <form onSubmit={add} className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Starts 1 July of</label>
          <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2027" inputMode="numeric"
            className="w-32 font-mono" data-testid="financial-year-start-input" />
        </div>
        <Button type="submit" data-testid="add-financial-year-button"><Plus className="h-4 w-4 mr-1" /> Add financial year</Button>
      </form>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="financial-year-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-5 py-3 font-medium">Year</th><th className="px-5 py-3 font-medium">Starts</th>
              <th className="px-5 py-3 font-medium">Ends</th><th className="px-5 py-3 font-medium">Status</th><th />
            </tr>
          </thead>
          <tbody>
            {(data || []).map((fy) => (
              <tr key={fy.id} className="border-b border-border last:border-0 hover:bg-accent/40 transition-colors" data-testid={`financial-year-row-${fy.label}`}>
                <td className="px-5 py-3 font-mono">{fy.label}</td>
                <td className="px-5 py-3 font-mono">{fmt(fy.start_date)}</td>
                <td className="px-5 py-3 font-mono">{fmt(fy.end_date)}</td>
                <td className="px-5 py-3">
                  {fy.is_current ? (
                    <span className="px-2 py-0.5 text-xs border border-emerald-500/40 text-emerald-300" data-testid={`financial-year-current-${fy.label}`}>Current</span>
                  ) : (
                    <Button variant="outline" size="sm" data-testid={`financial-year-set-current-${fy.label}`}
                      onClick={() => run(() => api("post", `/financial_years/${fy.id}/set-current`), `${fy.label} is now current`)}>
                      Make current
                    </Button>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <ConfirmDelete name={fy.label} testId={`financial-year-delete-${fy.label}`}
                    onConfirm={() => run(() => api("delete", `/financial_years/${fy.id}`), `${fy.label} deleted`)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
